'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import Image from 'next/image';
import type { SessionMode } from '@/hooks/useVoiceWidget';
import { PhoneShell } from './PhoneShell';
import VoiceRingCTA from '@/components/voice/VoiceRingCTA';
import VoiceRingFallback from '@/components/voice/VoiceRingFallback';
import { useDeferredVoiceRing } from './useDeferredVoiceRing';
import TalkToBriModalTrigger from '@/components/voice/TalkToBriModalTrigger';
import { V4_BRAND } from '@/lib/marketing/messaging-bank/v4-brand';
import { HERO_DEMO_SCRIPT } from '@/lib/marketing/messaging-bank/hero-demo-script';
import { pushEvent } from '@/lib/analytics/core';
import { readSessionMuted, useHeroDemoAudio, writeSessionMuted } from './useHeroDemoAudio';
import './hero-phone-demo.css';

/**
 * HeroPhoneDemo — the hero iPhone. Two modes, chosen by the page that mounts it.
 *
 * `mode="live"` (DEFAULT, 2026-07-31) — tapping the phone opens a REAL Bri
 * session (`TalkToBriModalTrigger`, source `hero_phone`). No transcript, no
 * voiceover, no post-call card: the phone IS the demo entry point.
 *
 * `mode="scripted"` — the original "watch Bri work" theater: a canned HVAC
 * transcript ported 1:1 from the approved mockup's phone state machine
 * (`PHONE_SCRIPT` / `pMode` / `pBubble` / `pLine` / `pReset`,
 * docs/design/mockups/homepage-v5-mockup.html), ending on a card that hands off
 * to the live modal. Reserved for the one surface whose audience matches the
 * script: /lp/ai-receptionist-hvac. The transcript is a dead-AC emergency, so it
 * is off-message even on the plumbing lander — do not widen this without
 * rewriting the script.
 *
 * WHY THE DEFAULT FLIPPED: the scripted demo was mounted on the homepage AND
 * every /lp/* lander via LandingTemplate, so law/dental/real-estate visitors met
 * an emergency-AC story. Worse, it dead-ended: 30 sessions tapped it in the 30
 * days to 2026-07-31 and 2 went on to a live call (PostHog `demo_started` vs
 * `voice_widget_click{source:hero_demo}`). Live mode removes the interstitial —
 * one tap to Bri instead of two, on every surface that isn't home services.
 *
 * IMPORTANT (scripted mode): it is theater. `VoiceRingCTA`'s `sessionMode` is
 * driven purely by local state stepping through the script — no ElevenLabs
 * session ever starts there. The only real voice session is the one
 * `TalkToBriModalTrigger` opens.
 *
 * VOICED DEMO (scripted mode): each script line has a pre-generated ElevenLabs
 * clip (HERO_DEMO_SCRIPT[].audioSrc), played by `useHeroDemoAudio` — clips are
 * created only on demo start (the tap is the user gesture that satisfies
 * autoplay policy) and each line advances on its clip's `ended` event, with
 * the pre-voiceover `talkMs` timers as the never-wedge fallback. See the
 * hook's docblock for the full failure-mode contract.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

type DemoPhase = 'idle' | 'running' | 'ended';

/** Which demo the hero phone runs — see the component docblock. */
export type HeroPhoneMode = 'live' | 'scripted';

export interface HeroPhoneDemoProps {
  /**
   * `live` (default) taps straight into a real Bri session. `scripted` plays the
   * canned HVAC transcript and is only correct where the audience matches it.
   */
  readonly mode?: HeroPhoneMode;
}

interface DemoBubble {
  id: number;
  who: 'caller' | 'bri';
  status: 'typing' | 'text';
  text: string;
}

const ISLAND_IDLE: ReactNode = (
  <>
    Bri · <em>ready</em>
  </>
);
const ISLAND_CONNECTING: ReactNode = 'Incoming call…';
const ISLAND_LISTENING: ReactNode = 'Bri · listening';
const ISLAND_SPEAKING: ReactNode = (
  <>
    Bri · responding
    <span className="hpd-island-bars" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </span>
  </>
);
const ISLAND_ENDED: ReactNode = 'Call ended · 28s';

const CALLER_STATUS_IDLE = 'Meet your concierge';
const CALLER_STATUS_RINGING = 'Ringing…';
const CALLER_STATUS_INCOMING = 'Incoming call · (619) 848-3370';
const CALLER_STATUS_SPEAKING = 'Bri is speaking';
const CALLER_STATUS_BOOKED = 'Booked: today 4–6pm';

const RING_SIZE = 158;
const CONNECT_DELAY_MS = 1200;
const END_DELAY_MS = 700;

/**
 * Renders a HERO_DEMO_SCRIPT line's text as React nodes. This is a
 * constrained parser for exactly one marker — literal `<b>…</b>` — NOT a
 * general HTML renderer, and it never uses `dangerouslySetInnerHTML`. It
 * must only ever be pointed at the compile-time HERO_DEMO_SCRIPT constant
 * this module imports, never at user input — see the invariant documented
 * on HERO_DEMO_SCRIPT itself.
 */
function renderScriptText(text: string): ReactNode[] {
  const boldTagRe = /<b>(.*?)<\/b>/g;
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  while ((match = boldTagRe.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
    }
    nodes.push(<b key={key++}>{match[1]}</b>);
    lastIndex = boldTagRe.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(<span key={key++}>{text.slice(lastIndex)}</span>);
  }
  return nodes;
}

function MicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
    </svg>
  );
}

function SpeakerOnIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 9v6h4l5 5V4L7 9H3z" />
      <path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v8.05A4.47 4.47 0 0 0 16.5 12z" />
      <path d="M14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
    </svg>
  );
}

function SpeakerOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.8 8.8 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z" />
    </svg>
  );
}

function PlayGlyphIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
      <path d="M3 1.6c0-.5.55-.8.98-.55l7 4.4c.4.25.4.85 0 1.1l-7 4.4A.65.65 0 0 1 3 10.4V1.6z" />
    </svg>
  );
}

/** A single bubble — mounts at opacity 0 then fades/slides in next frame,
 *  mirroring the mockup's `requestAnimationFrame(() => b.classList.add('on'))`. */
function Bubble({ bubble }: { bubble: DemoBubble }) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className={`hpd-bubble hpd-bubble--${bubble.who}${entered ? ' hpd-bubble--on' : ''}`}>
      {bubble.status === 'typing' ? (
        <span className="hpd-typing" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      ) : (
        renderScriptText(bubble.text)
      )}
    </div>
  );
}

export default function HeroPhoneDemo({ mode = 'live' }: HeroPhoneDemoProps = {}) {
  const live = mode === 'live';
  const [phase, setPhase] = useState<DemoPhase>('idle');
  const [sessionMode, setSessionMode] = useState<SessionMode>('idle');
  const [islandNode, setIslandNode] = useState<ReactNode>(ISLAND_IDLE);
  const [callerStatus, setCallerStatus] = useState(CALLER_STATUS_IDLE);
  const [bubbles, setBubbles] = useState<DemoBubble[]>([]);
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const [muted, setMuted] = useState(readSessionMuted);
  // Whether the CURRENT run has voiceover clips (drives the mute toggle's
  // visibility) — false when Audio is unavailable or reduced motion skipped
  // straight to the transcript.
  const [audioActive, setAudioActive] = useState(false);
  const { ringInteractive, stageRef, activateRing } = useDeferredVoiceRing();

  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const nextBubbleIdRef = useRef(0);
  const postCallCtaRef = useRef<HTMLButtonElement>(null);

  // Voiceover engine — clips are created only inside beginRun (post-tap);
  // scheduleTimer keeps every audio fallback timer in timersRef's sweep.
  const demoAudio = useHeroDemoAudio(scheduleTimer);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Timers live in a ref (not state) so they survive across the recursive
  // schedule chain and can be swept in one place — on replay and on unmount.
  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, []);

  function clearAllTimers() {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }

  function scheduleTimer(fn: () => void, ms: number) {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
  }

  function addBubble(who: DemoBubble['who']): number {
    const id = nextBubbleIdRef.current++;
    setBubbles((prev) => {
      const next = [...prev, { id, who, status: 'typing' as const, text: '' }];
      // Max 4 bubbles kept — mirrors the mockup's convo.children.length > 4 trim.
      return next.length > 4 ? next.slice(next.length - 4) : next;
    });
    return id;
  }

  function resolveBubble(id: number, text: string) {
    setBubbles((prev) => prev.map((b) => (b.id === id ? { ...b, status: 'text', text } : b)));
  }

  function finish() {
    setSessionMode('ended');
    setIslandNode(ISLAND_ENDED);
    setCallerStatus(CALLER_STATUS_BOOKED);
    setPhase('ended');
  }

  function playLine(i: number) {
    if (i >= HERO_DEMO_SCRIPT.length) {
      scheduleTimer(finish, END_DELAY_MS);
      return;
    }
    const line = HERO_DEMO_SCRIPT[i];
    if (line.who === 'caller') {
      setSessionMode('listening');
      setIslandNode(ISLAND_LISTENING);
      setCallerStatus(CALLER_STATUS_INCOMING);
    } else {
      setSessionMode('speaking');
      setIslandNode(ISLAND_SPEAKING);
      setCallerStatus(CALLER_STATUS_SPEAKING);
    }
    const bubbleId = addBubble(line.who);
    scheduleTimer(() => {
      resolveBubble(bubbleId, line.text);
      // Bubble text and its voiceover clip start together; the clip's
      // `ended` event (or the talkMs fallback) advances to the next line.
      demoAudio.startLineAdvance(i, line, () => playLine(i + 1));
    }, line.thinkMs);
  }

  /** No timers at all for reduced motion — render the final transcript and
   *  ended state synchronously in one state update. */
  function finishReducedMotion() {
    setBubbles(
      HERO_DEMO_SCRIPT.map((line, index) => ({
        id: index,
        who: line.who,
        status: 'text' as const,
        text: line.text,
      })),
    );
    finish();
  }

  function beginDemo() {
    activateRing();
    pushEvent('demo_started', { source: 'hero_demo' });
    clearAllTimers();
    setBubbles([]);
    setPhase('running');
    setSessionMode('connecting');
    setIslandNode(ISLAND_CONNECTING);
    setCallerStatus(CALLER_STATUS_RINGING);

    if (reducedMotion) {
      setAudioActive(false);
      finishReducedMotion();
      return;
    }
    setAudioActive(demoAudio.beginRun(muted));
    scheduleTimer(() => playLine(0), CONNECT_DELAY_MS);
  }

  function handleMuteToggle() {
    const next = !muted;
    setMuted(next);
    writeSessionMuted(next);
    demoAudio.applyMuted(next);
  }

  function handleTap(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    if (phase !== 'idle') return;
    beginDemo();
  }

  function handleReplay() {
    clearAllTimers();
    beginDemo();
  }

  // Once the demo ends, the full-area tap `<a>` is visually covered by the
  // post-call overlay but stays mounted — without this it remains a
  // focusable dead tab stop right before the card's own buttons. Move focus
  // to the post-call card's primary CTA instead.
  useEffect(() => {
    if (phase === 'ended') {
      postCallCtaRef.current?.focus({ preventScroll: true });
    }
  }, [phase]);

  const islandContent = (
    <>
      <span className="hpd-island-lens" aria-hidden="true" />
      <span className="hpd-island-status" data-mode={sessionMode}>
        {islandNode}
      </span>
    </>
  );

  const renderPhone = (onTap: (event: React.MouseEvent<HTMLAnchorElement>) => void) => (
    <div className="hpd-root" data-testid="hero-phone-demo" data-phone-mode={mode}>
      <PhoneShell size="hero" islandContent={islandContent}>
        <div className="hpd-stage" data-testid="voice-widget-container" data-mode={sessionMode}>
          <a
            ref={stageRef}
            href={V4_BRAND.voiceFallbackHref}
            className="hpd-tap-link"
            data-testid="hero-phone-stage-fallback"
            data-hero-voice-trigger="phone"
            onClick={onTap}
            tabIndex={phase === 'ended' ? -1 : undefined}
          >
            <div className="hpd-caller">
              <div className="hpd-caller-status">{callerStatus}</div>
              <div className="hpd-caller-name">Bri</div>
              <div className="hpd-caller-num">BizRnR · AI Voice Agent</div>
            </div>
            <div
              className="hpd-ring-mount"
              aria-hidden="true"
              inert
              style={{ width: RING_SIZE, height: RING_SIZE }}
            >
              {/* Beam arrival pulse — the tether beam's energy visibly lands in
                  the ring center. Only rendered where the beam exists (≥1100px,
                  CSS-gated) and only while idle; synced to the beam's 2.4s
                  pulse cycle. */}
              <span className="hpd-ring-arrival" />
              {/* Idle attention ripples — CSS keys the animation on
                  .hpd-stage[data-mode='idle'], so these stop the moment the
                  demo starts. Timed to burst at the ghost-tap press moment. */}
              <span className="hpd-ring-ripple" />
              <span className="hpd-ring-ripple hpd-ring-ripple--late" />
              {ringInteractive ? (
                <VoiceRingCTA size={RING_SIZE} sessionMode={sessionMode} />
              ) : (
                <VoiceRingFallback size={RING_SIZE} sessionMode={sessionMode} interactive={false} />
              )}
              {/* Ghost tap — a fingertip drifts in and taps the ring every
                  cycle so first-time visitors read the phone as tappable.
                  Decorative; the anchor carries the real affordance. */}
              <span className="hpd-ghost-tap">
                <span className="hpd-ghost-tap-tip" />
                <span className="hpd-ghost-tap-burst" />
              </span>
            </div>
            <div className="hpd-convo" aria-live="polite">
              {bubbles.map((bubble) => (
                <Bubble key={bubble.id} bubble={bubble} />
              ))}
            </div>
            {/* BizRnR app dock — iOS home-screen style app icon anchoring the
                lower screen while idle; the labeled entry point for the demo.
                Decorative inside the full-stage tap anchor. */}
            <span
              className={`hpd-app-dock${phase !== 'idle' ? ' hpd-app-dock--hidden' : ''}`}
              data-testid="hero-demo-tap-cta"
            >
              <span className="hpd-app-icon" aria-hidden="true">
                <Image
                  src="/logos/bizrnr-app-icon.png"
                  alt=""
                  aria-hidden="true"
                  width={62}
                  height={62}
                  priority={false}
                />
                <span className="hpd-app-icon-shine" />
                <span className="hpd-app-icon-halo" />
              </span>
              <span className="hpd-app-label">
                <PlayGlyphIcon />
                {live ? 'Tap to Talk to Bri' : 'Tap to Demo'}
              </span>
            </span>
          </a>

          {/* Mute toggle — sibling of the tap anchor (never a nested
              interactive control). Only meaningful while the voiced run is
              playing; muted state persists for the session. */}
          {phase === 'running' && audioActive && (
            <button
              type="button"
              className="hpd-mute"
              onClick={handleMuteToggle}
              aria-pressed={muted}
              aria-label={muted ? 'Unmute demo audio' : 'Mute demo audio'}
              data-testid="hero-demo-mute"
            >
              {muted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
            </button>
          )}

          {phase === 'ended' && (
            <div className="hpd-postcall hpd-postcall--on" data-testid="hero-phone-postcall">
              <div className="hpd-postcall-card">
                <p className="hpd-postcall-eyebrow">Demo complete</p>
                <p className="hpd-postcall-title">That&apos;s Bri handling a real call.</p>
                <p className="hpd-postcall-body">
                  Now hear her yourself — she&apos;ll answer <b>your</b> questions live.
                </p>
                <TalkToBriModalTrigger source="hero_demo">
                  {(open) => (
                    <button
                      type="button"
                      className="hpd-postcall-cta"
                      onClick={open}
                      ref={postCallCtaRef}
                    >
                      <MicIcon />
                      Talk to Bri yourself
                    </button>
                  )}
                </TalkToBriModalTrigger>
                <button type="button" className="hpd-postcall-replay" onClick={handleReplay}>
                  ↻ Replay the call
                </button>
              </div>
            </div>
          )}
        </div>
      </PhoneShell>
    </div>
  );

  // Live mode: the tap IS the demo. The `tel:` href stays on the anchor as the
  // no-JS / no-mic fallback, exactly as in scripted mode — we only replace what
  // the click does.
  if (live) {
    return (
      <TalkToBriModalTrigger source="hero_phone">
        {(open) =>
          renderPhone((event) => {
            event.preventDefault();
            open();
          })
        }
      </TalkToBriModalTrigger>
    );
  }

  return renderPhone(handleTap);
}
